import { useCallback, useRef, useState } from 'react';
import { useMatterJurisdiction } from '@/components/contexts/MatterJurisdictionContext';
import {
  ChronologyFilterBar,
  type ChronologyViewMode,
  ViewModeToggle,
} from '@/components/knowledge-graph/chronology-panel/ChronologyFilterBar';
import {
  ChronologyFilterSummary,
  type ChronologyRefreshStatus,
  RefreshStatus,
} from '@/components/knowledge-graph/chronology-panel/ChronologyFilterSummary';
import { ExportPopover } from '@/components/knowledge-graph/chronology-panel/ExportPopover';
import { HorizontalTimeline } from '@/components/knowledge-graph/chronology-panel/HorizontalTimeline';
import { useChronologySearchHighlightSource } from '@/components/knowledge-graph/chronology-panel/search-highlight';
import { ChronologyTimeline } from '@/components/knowledge-graph/chronology-panel/timeline';
import { TOOLBAR_CONTAINER_CLASSNAME } from '@/components/knowledge-graph/chronology-panel/toolbar-styles';
import { useChronologyFilters } from '@/components/knowledge-graph/chronology-panel/useChronologyFilters';
import { OutputDisclaimer } from '@/components/shared/OutputDisclaimer';
import type { ChronologyInfo } from '@/hooks/useChronologies';
import type { ChronologyEventLight } from '@/hooks/useChronology';
import { cn } from '@/lib/utils';

export { getCategoryStyle, PALETTE } from '@/components/knowledge-graph/chronology-panel/styles';

const VIEW_MODE_STORAGE_KEY = 'crimson.chronology.viewMode';

function readStoredViewMode(): ChronologyViewMode {
  try {
    return window.localStorage.getItem(VIEW_MODE_STORAGE_KEY) === 'timeline' ? 'timeline' : 'list';
  } catch {
    return 'list';
  }
}

interface ChronologyPanelProps {
  events: ChronologyEventLight[];
  onEventSelect?: (event: ChronologyEventLight | null) => void;
  selectedEventId?: string | null;
  matterGuid?: string;
  chronologyId?: string | null;
  description?: string;
  refreshStatus?: ChronologyRefreshStatus | null;
  colorByCategory?: ReadonlyMap<string, string | null>;
  chronologies?: ChronologyInfo[];
  searchQuery?: string;
  onSearchChange?: (value: string) => void;
  /** Puts the events on their own white section below the controls (full-page use). */
  framed?: boolean;
  /** Keeps this panel on one view and leaves the saved chronology preference alone. */
  lockedViewMode?: ChronologyViewMode;
  /** Renders the event list without search, filters, export, or the output disclaimer. */
  hideControls?: boolean;
  /** The chronology page can switch to the timeline. Side panels stay on the list. */
  allowTimeline?: boolean;
}

export function ChronologyPanel({
  events,
  onEventSelect,
  refreshStatus,
  colorByCategory,
  chronologies = [],
  selectedEventId,
  matterGuid,
  chronologyId = null,
  description,
  searchQuery: searchQueryProp,
  onSearchChange,
  framed = false,
  lockedViewMode,
  hideControls = false,
  allowTimeline = true,
}: ChronologyPanelProps) {
  const { locale } = useMatterJurisdiction();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [storedViewMode, setStoredViewMode] = useState<ChronologyViewMode>(() => (
    allowTimeline ? readStoredViewMode() : 'list'
  ));
  const viewMode: ChronologyViewMode = lockedViewMode ?? (allowTimeline ? storedViewMode : 'list');
  const [timelineControlsHost, setTimelineControlsHost] = useState<HTMLDivElement | null>(null);
  const isSelectionControlled = selectedEventId !== undefined;
  const effectiveSelectedId = isSelectionControlled ? selectedEventId : selectedId;
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const filters = useChronologyFilters({
    events,
    chronologies,
    searchQueryProp,
    onSearchChange,
  });
  const isAggregate = chronologies.length > 1 || (chronologies.length === 0 && filters.chronologyOptions.length > 1);

  useChronologySearchHighlightSource(filters.searchQuery);

  const setViewMode = useCallback((mode: ChronologyViewMode) => {
    if (lockedViewMode) return;
    setStoredViewMode(mode);
    try {
      window.localStorage.setItem(VIEW_MODE_STORAGE_KEY, mode);
    } catch {
      // Storage can be unavailable (private mode); the choice just won't persist.
    }
  }, [lockedViewMode]);

  const handleSelect = useCallback((event: ChronologyEventLight) => {
    const next = effectiveSelectedId === event.id ? null : event.id;
    if (!isSelectionControlled) setSelectedId(next);
    onEventSelect?.(next ? event : null);
  }, [effectiveSelectedId, isSelectionControlled, onEventSelect]);

  const showUndatedInList = useCallback(() => {
    filters.setShowUndated(true);
    setViewMode('list');
  }, [filters.setShowUndated, setViewMode]);

  const hasEvents = events.length > 0;
  const noMatches = hasEvents && filters.filteredEvents.length === 0;
  // Event list only. The filter bar stays on the page inset; the detail panel has no nav beside it.
  const contentGutter = framed ? 'pl-9 pr-6' : 'px-6';

  return (
    <div className="flex h-full flex-col">
      {!hideControls && (description || hasEvents) && (
        <div className={cn(TOOLBAR_CONTAINER_CLASSNAME, 'shrink-0 space-y-3 px-6 pt-3.5 pb-3', !framed && 'border-b border-gray-100')}>
          {(description || refreshStatus) && (
            <div className="flex items-end justify-end gap-8">
              {description && (
                <p title={description} className="mr-auto min-w-0 max-w-[52rem] truncate text-[13px] leading-5 text-gray-500">
                  {description}
                </p>
              )}
              {refreshStatus && <RefreshStatus status={refreshStatus} />}
            </div>
          )}
          {hasEvents && (
            <ChronologyFilterBar
              filters={filters}
              locale={locale}
              colorByCategory={colorByCategory}
              showChronologyFilter={isAggregate && filters.chronologyOptions.length > 1}
              trailing={(
                <>
                  {allowTimeline && !lockedViewMode && <ViewModeToggle value={viewMode} onChange={setViewMode} />}
                  <ExportPopover
                    matterGuid={matterGuid}
                    chronologyId={chronologyId}
                    filteredEvents={filters.filteredEvents}
                    locale={locale}
                  />
                </>
              )}
            />
          )}
        </div>
      )}

      <div
        className={cn(
          'flex min-h-0 flex-1 flex-col',
          framed && 'border-t border-slate-900/[0.08] bg-white [--chron-surface:white]',
        )}
      >
        {!hideControls && hasEvents && (
          <div className={cn('shrink-0 py-2', contentGutter)}>
            <ChronologyFilterSummary
              eventsCount={events.length}
              filteredEventsCount={filters.filteredEvents.length}
              hasActiveFilters={filters.hasActiveFilters}
              onClearAllFilters={filters.clearFilters}
              afterCount={viewMode === 'timeline' ? (
                <div ref={setTimelineControlsHost} className="contents" />
              ) : null}
              trailing={<OutputDisclaimer className="shrink-0" />}
            />
          </div>
        )}

        {!hasEvents ? (
          <div className={cn('py-12 text-center text-sm text-gray-400', contentGutter)}>No events extracted yet.</div>
        ) : noMatches ? (
          <div className={cn('py-12 text-center text-gray-400', contentGutter)}>
            <p className="text-sm">No events match the current filters.</p>
            <button type="button" onClick={filters.clearFilters} className="mt-1 text-sm font-medium text-gray-500 hover:text-gray-700">Clear filters</button>
          </div>
        ) : viewMode === 'timeline' ? (
          <div className={cn('min-h-0 flex-1', framed && 'pl-3')}>
            <HorizontalTimeline
              events={filters.datedEvents}
              undatedCount={filters.undatedEvents.length}
              selectedEventId={effectiveSelectedId}
              onSelectEvent={handleSelect}
              onShowUndated={showUndatedInList}
              colorByCategory={colorByCategory}
              activeCategories={filters.activeCategories}
              excludedTypes={filters.excludedTypes}
              isAggregate={isAggregate}
              locale={locale}
              controlsHost={timelineControlsHost}
            />
          </div>
        ) : (
          <div
            ref={scrollContainerRef}
            className="subtle-scrollbar min-h-0 flex-1 overflow-y-scroll"
            style={{ overflowAnchor: 'none' }}
          >
            <div className={cn('pb-4', contentGutter, hideControls && 'pt-3')}>
              <ChronologyTimeline
                datedEvents={filters.datedEvents}
                undatedEvents={filters.undatedEvents}
                selectedEventId={effectiveSelectedId}
                onSelectEvent={handleSelect}
                colorByCategory={colorByCategory}
                activeCategories={filters.activeCategories}
                excludedTypes={filters.excludedTypes}
                isAggregate={isAggregate}
                locale={locale}
                showUndated={filters.showUndated}
                onToggleUndated={() => filters.setShowUndated(value => !value)}
                onShowUndated={() => filters.setShowUndated(true)}
                scrollContainerRef={scrollContainerRef}
                motionScope={chronologyId ?? 'all'}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
